import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, Heart, Filter, Grid, List, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { API } from '@/config/api';

// Updated categories to match your actual products
const categories = ['All', 'Apparel', 'Outerwear', 'Headwear', 'Drinkware', 'Accessories'];

export default function FashionHouse() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [viewMode, setViewMode] = useState('grid');
  
  useEffect(() => {
    fetchProducts();
  }, []);
  
  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/fashion/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Fetch products error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(p => p.category === selectedCategory);
  
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };
  
  const handleAddToWishlist = (product) => {
    toast.success(`${product.name} added to wishlist`);
  };
  
  const handleShopOnSite = () => {
    window.open('https://www.nouveaurichelegacy.com/shop', '_blank');
  };
  
  return (
    <div className="min-h-screen bg-[#0d0c0a] pt-24 pb-12" data-testid="fashion-house">
      {/* Hero - Tech Artsy Design */}
      <section className="relative h-[60vh] overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: 'url(https://static.wixstatic.com/media/95e2db_920cb95fb9944c3d8d0542174f4f2689~mv2.png)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0c0a] via-[#0d0c0a]/80 to-transparent" />
          {/* Grid overlay */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `
              linear-gradient(to right, #D4AF37 1px, transparent 1px),
              linear-gradient(to bottom, #D4AF37 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative z-10 h-full flex flex-col justify-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full w-fit mb-6">
            <ShoppingBag className="h-4 w-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-mono uppercase tracking-wider">Official Merchandise</span>
          </div>
          
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#FAFAFA] mb-4">
            House of<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#F5E6A3]">
              Nouveau Riche
            </span>
          </h1>
          
          <p className="font-body text-lg text-[#FAFAFA]/70 max-w-xl mb-8">
            Wear your legacy. Premium apparel and merchandise designed for those building generational wealth.
          </p>
          
          <Button 
            onClick={handleShopOnSite}
            className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90 font-medium px-8 py-6 w-fit"
            data-testid="shop-on-site-btn"
          >
            <ExternalLink className="mr-2 h-5 w-5" />
            Shop on Official Site
          </Button>
        </div>
      </section>
      
      {/* Filters - Dark Theme */}
      <section className="sticky top-20 z-40 bg-[#0d0c0a]/95 backdrop-blur-md border-y border-[#D4AF37]/20 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              <Filter className="h-5 w-5 text-[#D4AF37] flex-shrink-0" />
              <div className="flex gap-2">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                    className={`whitespace-nowrap ${selectedCategory === cat 
                      ? 'bg-[#D4AF37] text-[#1c1814]' 
                      : 'border-[#D4AF37]/30 text-[#FAFAFA] hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]'
                    }`}
                    data-testid={`filter-${cat.toLowerCase()}`}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#FAFAFA]/50 font-mono">{filteredProducts.length} products</span>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('grid')}
                className={viewMode === 'grid' ? 'text-[#D4AF37]' : 'text-[#FAFAFA]/50'}
              >
                <Grid className="h-5 w-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'text-[#D4AF37]' : 'text-[#FAFAFA]/50'}
              >
                <List className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* Products Grid - Dark Theme */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className={viewMode === 'grid' 
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8'
              : 'space-y-6'
            }>
              {filteredProducts.map((product) => (
                <Card 
                  key={product.product_id}
                  className={`bg-[#1c1814] border-[#D4AF37]/20 hover:border-[#D4AF37]/50 group overflow-hidden transition-all ${viewMode === 'list' ? 'flex' : ''}`}
                  data-testid={`product-${product.product_id}`}
                >
                  {/* Image */}
                  <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-48 flex-shrink-0' : 'aspect-square'}`}>
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {!product.in_stock && (
                      <div className="absolute inset-0 bg-[#1c1814]/70 flex items-center justify-center">
                        <Badge variant="secondary" className="bg-[#FAFAFA] text-[#1c1814]">Out of Stock</Badge>
                      </div>
                    )}
                    <button
                      onClick={() => handleAddToWishlist(product)}
                      className="absolute top-4 right-4 p-2 bg-[#FAFAFA] rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:scale-110"
                      data-testid={`wishlist-${product.product_id}`}
                    >
                      <Heart className="h-5 w-5 text-[#1c1814]" />
                    </button>
                  </div>
                  
                  {/* Content */}
                  <CardContent className={`p-6 ${viewMode === 'list' ? 'flex-1 flex flex-col justify-center' : ''}`}>
                    <Badge variant="outline" className="mb-2 text-xs uppercase tracking-wider border-[#D4AF37]/30 text-[#FAFAFA]/60">
                      {product.category}
                    </Badge>
                    <h3 className="font-display text-lg text-[#FAFAFA] mb-2">{product.name}</h3>
                    <p className="font-body text-sm text-[#FAFAFA]/60 mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                      <p className="font-display text-xl text-[#D4AF37]">{formatPrice(product.price)}</p>
                      <Button 
                        size="sm"
                        disabled={!product.in_stock}
                        onClick={handleShopOnSite}
                        className="bg-[#D4AF37] text-[#1c1814] hover:bg-[#D4AF37]/90"
                        data-testid={`buy-${product.product_id}`}
                      >
                        <ShoppingBag className="h-4 w-4 mr-2" />
                        Buy Now
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="h-16 w-16 text-[#FAFAFA]/20 mx-auto mb-4" />
              <p className="font-body text-[#FAFAFA]/60">No products found in this category</p>
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-[#1c1814]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-[#FAFAFA] mb-4">
            Custom <span className="text-[#D4AF37]">Branding</span>
          </h2>
          <p className="font-body text-[#FAFAFA]/70 mb-8">
            Looking for custom merchandise for your brand or business? Let's create something unique together.
          </p>
          <Button 
            onClick={() => window.open('https://www.nouveaurichelegacy.com/booking-calendar/business-consultation', '_blank')}
            className="btn-gold" 
            data-testid="custom-order-btn"
          >
            Request Custom Order
          </Button>
        </div>
      </section>
    </div>
  );
}
